import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '@redux/actions/utility-actions';
import { useClickOutside } from '../hooks/useClickOutside';
import { useMemoizedMenu } from '../hooks/useMemoizedMenu';
import { MENU_CONFIG } from '../constants/menuConfig';
import { useNavigationContext } from '../context/NavigationContext';
import SidebarHeader from './SidebarHeader';
import MenuItem from './MenuItem';
import packageJson from '@root/package.json';

/**
 * Modern Collapsible Sidebar Navigation Component
 * 
 * Features:
 * - Collapsible sidebar with smooth animations
 * - Main menu items with icons
 * - Sub-menu items without icons (nested)
 * - Role-based visibility
 * - Performance optimized with memoization
 * - Click outside to close
 * - Responsive design
 * 
 * @component
 * @example
 * <Sidebar />
 * 
 * Optimized: Removed Redux connect(), added useMemo for computed values
 */
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get navigation context
  const { isSidebarOpen, closeSidebar, _internal } = useNavigationContext();
  const { 
    activeItemId, 
    setActive, 
    expand, 
    isExpanded, 
    toggleExpanded 
  } = _internal;
  
  const menuItems = useMemoizedMenu(MENU_CONFIG);

  // Memoize app version
  const appVersion = useMemo(() => packageJson.version, []);

  // Click outside to close sidebar
  const sidebarRef = useClickOutside(() => {
    if (isSidebarOpen) {
      closeSidebar();
    }
  });

  // Find active menu item based on current route
  useEffect(() => {
    const currentPath = location.pathname;

    // Check all menu items and sub-items
    for (const item of menuItems) {
      if (item.to === currentPath) {
        setActive(item.id);
        return;
      }

      if (item.children) {
        for (const child of item.children) {
          if (child.to === currentPath) {
            setActive(child.id);
            expand(item.id); // Auto-expand parent
            return;
          }
        }
      }
    }
  }, [location.pathname, menuItems, setActive, expand]);

  // Handle menu item click
  const handleItemClick = useCallback(
    (item) => {
      if (item.action === 'logout') {
        dispatch(logout());
        closeSidebar();
      } else if (item.action === 'divisionGroup') {
        localStorage.setItem('pop_status', 2);
        dispatch({ type: 'SHOW_DIVISION_GROUP_MODAL' });
        closeSidebar();
      } else if (item.to) {
        setActive(item.id);
        closeSidebar();
        navigate(item.to);
      }
    },
    [navigate, setActive, dispatch, closeSidebar]
  );

  // Handle sub-item click
  const handleSubItemClick = useCallback(
    (item) => {
      setActive(item.id);
      closeSidebar();
      navigate(item.to);
    },
    [navigate, setActive, closeSidebar]
  );

  // Don't render if sidebar is closed
  if (!isSidebarOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          fixed top-0 left-0 h-full bg-white shadow-2xl z-[70]
          transform transition-transform duration-350 ease-in-out
          flex flex-col
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ 
          width: '100%',
          maxWidth: '20.188rem'
        }}
        aria-label="Main navigation"
      >
        {/* Header */}
        <SidebarHeader />

        {/* Menu Title */}
        <div 
          style={{
            fontFamily: 'Maven Pro, sans-serif',
            fontWeight: 500,
            fontSize: '1.063rem',
            lineHeight: '1.25rem',
            color: '#979797',
            padding: '1.25rem'
          }}
        >
          Menu
        </div>

        {/* Menu Items */}
        <nav 
          className="flex-1 overflow-y-auto"
          style={{
            scrollbarWidth: 'thin'
          }}
        >
          <ul style={{ padding: '0.5rem 0' }} role="menu">
            {menuItems.map((item) => {
              const childIsActive = item.children?.some(c => c.id === activeItemId);
              return (
              <MenuItem
                key={item.id}
                item={item}
                isActive={activeItemId === item.id || childIsActive}
                activeItemId={activeItemId}
                isExpanded={isExpanded(item.id)}
                onToggleExpand={toggleExpanded}
                onItemClick={handleItemClick}
                onSubItemClick={handleSubItemClick}
              />
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div 
          style={{
            textAlign: 'center',
            padding: '1.25rem 2rem',
            borderTop: '1px solid #e6e6e6',
            fontFamily: 'Maven Pro, sans-serif',
            fontWeight: 500,
            fontSize: '12.3603px',
            lineHeight: '1.188rem',
            color: '#adadad'
          }}
        >
          M-Connect v{appVersion}
        </div>
      </aside>
    </>
  );
};

export default React.memo(Sidebar);
